import React, { useEffect, useRef, useState } from "react";
import { http } from "../../axios/axios";
import ServiceCard from "./ServiceCard";
import { Box } from "lucide-react";
import Pagination from "../Pagination";

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const cardsSectionRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalPages: 1,
  });

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);

      try {
        const res = await http.get(
          `/services?page=${pagination.page}&limit=${pagination.limit}`
        );

        setServices(res.data?.data || []);

        const totalPages = res.data?.totalPages || 1;

        setPagination((prev) => ({
          ...prev,
          page: prev.page > totalPages ? 1 : prev.page,
          totalPages,
        }));
      } catch (err) {
        console.error(
          "Error fetching services:",
          err.response?.data || err.message
        );

        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();

    if (hasLoadedRef.current) {
      cardsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      hasLoadedRef.current = true;
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [pagination.page, pagination.limit]);

  const changePage = (page) => {
    if (
      page < 1 ||
      page > pagination.totalPages ||
      page === pagination.page
    ) {
      return;
    }

    setPagination((prev) => ({
      ...prev,
      page,
    }));
  };

  return (
    <main className="min-h-screen bg-white">
    
      {/* Page Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-10 pb-6 text-center">
        <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-blue-600">
          Our Services
        </p>

        <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
          Security & IT Services
        </h1>

        <p className="mt-3 max-w-3xl mx-auto text-sm sm:text-base text-slate-500 leading-relaxed">
          Explore our professional security surveillance and IT services
          designed for homes, offices, societies, hospitals, schools,
          industries and commercial environments.
        </p>
      </section>

      {/* Services */}
      <section
        ref={cardsSectionRef}
        className="px-4 sm:px-6 lg:px-8 pb-12"
      >
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-700" />
          </div>
        ) : services.length > 0 ? (
          <>
            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                lg:grid-cols-4
                gap-6
                max-w-7xl
                mx-auto
              "
            >
              {services.map((service, index) => (
                <ServiceCard
                  key={service._id || index}
                  service={service}
                />
              ))}
            </div>

            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={changePage}
            />
          </>
        ) : (
          <div
            className="
              max-w-7xl
              mx-auto
              py-20
              text-center
              border
              border-dashed
              border-slate-300
              rounded-2xl
              bg-slate-50
            "
          >
            <Box
              size={40}
              className="mx-auto text-slate-300 mb-4"
            />

            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              No Services Available
            </h3>

            <p className="text-sm text-slate-500">
              We are currently updating our services. Please check back soon.
            </p>
          </div>
        )}
      </section>
    </main>
  );
};

export default ServicesPage;